// src/pages/PLC.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Box, Paper, Typography, Button, Chip, Stack, Grid, Divider, Skeleton,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import api from "../api";

const MAX_VARS = 5;

function prettyLabel(variable) {
  if (!variable) return "";
  return variable
    .replace(/^AIDI\./, "")
    .replace(/_/g, " ")
    .replace(/\bVal\b/g, "Value")
    .replace(/\bTemp\b/g, "Temp")
    .replace(/\bPress\b/g, "Pressure")
    .replace(/\bOffs\b/g, "Offset")
    .replace(/\bDscg\b/g, "Dscg")
    .replace(/\bSuct\b/g, "Suct")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value !== "number") return String(value);
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.00$/, "");
}

function formatVarValue(value, meta) {
  if (meta?.is_boolean) {
    if (value === null || value === undefined) return "—";
    return Number(value) === 0
      ? (meta.bool_false_label || "Off")
      : (meta.bool_true_label || "On");
  }
  return formatValue(value);
}

function timeAgo(ts) {
  if (!ts) return null;
  const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 10) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function getStatus(latestValues) {
  const timestamps = Object.values(latestValues || {})
    .map((dp) => (dp?.timestamp ? new Date(dp.timestamp).getTime() : 0))
    .filter(Boolean);
  if (timestamps.length === 0) return "unknown";
  return (Date.now() - Math.max(...timestamps)) <= 3 * 60 * 1000 ? "online" : "offline";
}

// ── Stat tile ─────────────────────────────────────────────────────────────────

function StatTile({ label, value, tone }) {
  const tones = {
    green:   { color: "#16a34a", bg: "rgba(34,197,94,0.10)",  border: "rgba(34,197,94,0.30)" },
    red:     { color: "#dc2626", bg: "rgba(239,68,68,0.10)",  border: "rgba(239,68,68,0.30)" },
    default: { color: "var(--text-main)", bg: "var(--bg-elevated)", border: "var(--border-color)" },
  };
  const t = tones[tone] || tones.default;
  return (
    <Box sx={{
      px: 2.25, py: 1.25, minWidth: 92,
      borderRadius: "var(--card-radius-sm)",
      background: t.bg, border: `1px solid ${t.border}`,
    }}>
      <Typography sx={{
        fontFamily: "var(--font-display)", fontWeight: 700,
        fontSize: "1.55rem", lineHeight: 1.15, color: t.color,
      }}>
        {value}
      </Typography>
      <Typography sx={{
        color: "var(--text-muted)", fontWeight: 600, letterSpacing: "0.05em",
        textTransform: "uppercase", fontSize: "0.62rem",
      }}>
        {label}
      </Typography>
    </Box>
  );
}

// ── PLC Card — fixed 300px height ────────────────────────────────────────────

function PLCCard({ plc, latestValues, importantVars, onOpen }) {
  const varList =
    importantVars?.length > 0
      ? importantVars.map((v) => (typeof v === "string" ? { variable_name: v } : v))
      : Object.keys(latestValues || {}).map((v) => ({ variable_name: v }));

  const mostRecentTs = useMemo(() => {
    const timestamps = Object.values(latestValues || {})
      .map((x) => (x?.timestamp ? new Date(x.timestamp).getTime() : 0))
      .filter(Boolean);
    return timestamps.length ? Math.max(...timestamps) : null;
  }, [latestValues]);

  const status       = getStatus(latestValues);
  const visibleVars  = varList.slice(0, MAX_VARS);
  const hiddenCount  = varList.length - visibleVars.length;

  const dotColor   = status === "online" ? "#22c55e" : status === "offline" ? "#ef4444" : "#9ca3af";
  const chipColor  = status === "online" ? { bg: "rgba(34,197,94,0.10)",  border: "rgba(34,197,94,0.35)",  text: "#16a34a" }
                   : status === "offline" ? { bg: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.35)",  text: "#dc2626" }
                   : { bg: "rgba(156,163,175,0.12)", border: "rgba(156,163,175,0.35)", text: "#6b7280" };
  const chipLabel  = status === "online" ? "Online" : status === "offline" ? "Offline" : "No data";
  const statusBar  = status === "online"  ? "linear-gradient(90deg, #16a34a, #4ade80)"
                   : status === "offline" ? "linear-gradient(90deg, #dc2626, #f87171)"
                   : "var(--border-color)";

  return (
    <Paper
      elevation={0}
      onClick={onOpen}
      sx={{
        height: 300,
        display: "flex",
        flexDirection: "column",
        borderRadius: "var(--card-radius)",
        border: "1px solid var(--border-color)",
        backgroundColor: "var(--bg-elevated)",
        boxShadow: "var(--shadow-soft)",
        cursor: "pointer",
        overflow: "hidden",
        transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "var(--shadow-medium)",
          borderColor: "var(--accent)",
        },
      }}
    >
      {/* status strip */}
      <Box sx={{ height: 3, flexShrink: 0, background: statusBar }} />
      {/* ── Header ── */}
      <Box sx={{ px: 2.25, pt: 2.25, pb: 1.5, flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0, mr: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: dotColor, flexShrink: 0,
                  ...(status === "online" && {
                    "@keyframes livePulse": {
                      "0%,100%": { opacity: 1, transform: "scale(1)" },
                      "50%": { opacity: 0.45, transform: "scale(0.72)" },
                    },
                    animation: "livePulse 1.8s ease-in-out infinite",
                  }),
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700, color: "var(--text-main)", lineHeight: 1.25,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}
              >
                {plc.name}
              </Typography>
            </Stack>

            {plc.location && (
              <Stack direction="row" spacing={0.4} alignItems="center" sx={{ mt: 0.4 }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 12, color: "var(--text-muted)" }} />
                <Typography variant="caption" sx={{ color: "var(--text-muted)", lineHeight: 1 }}>
                  {plc.location}
                </Typography>
              </Stack>
            )}
          </Box>

          <Box
            sx={{
              px: 1, py: 0.35, borderRadius: 999, flexShrink: 0,
              fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.04em",
              background: chipColor.bg, border: `1px solid ${chipColor.border}`, color: chipColor.text,
            }}
          >
            {chipLabel}
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: "var(--border-color)" }} />

      {/* ── Variable rows — flex:1 clips overflow, no scroll ── */}
      <Box sx={{ flex: 1, px: 2.25, py: 1.25, overflow: "hidden" }}>
        {varList.length > 0 ? (
          <>
            {visibleVars.map((meta, idx) => {
              const val = latestValues?.[meta.variable_name]?.value;
              return (
                <Stack
                  key={meta.variable_name}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    py: "5px",
                    borderBottom:
                      idx < visibleVars.length - 1 || hiddenCount > 0
                        ? "1px solid var(--border-color)"
                        : "none",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "var(--text-muted)", fontSize: "0.79rem",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      mr: 1, maxWidth: "64%",
                    }}
                  >
                    {meta.display_name || prettyLabel(meta.variable_name)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, fontSize: "0.83rem", color: "var(--text-main)", flexShrink: 0 }}
                  >
                    {formatVarValue(val, meta)}
                  </Typography>
                </Stack>
              );
            })}
            {hiddenCount > 0 && (
              <Typography variant="caption" sx={{ color: "var(--text-muted)", display: "block", pt: 0.75 }}>
                +{hiddenCount} more variable{hiddenCount !== 1 ? "s" : ""}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: "var(--text-muted)", pt: 0.5 }}>
            No variables configured.
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: "var(--border-color)" }} />

      {/* ── Footer ── */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 2.25, py: 1.25, flexShrink: 0 }}
      >
        <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>
          {mostRecentTs ? timeAgo(mostRecentTs) : "No data yet"}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowForwardIcon sx={{ fontSize: "14px !important" }} />}
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          sx={{ borderRadius: 999, py: 0.3, fontSize: "0.72rem", minHeight: 0, lineHeight: 1.2 }}
        >
          Open
        </Button>
      </Stack>
    </Paper>
  );
}

// ── Client section ────────────────────────────────────────────────────────────

function PLCSection({ title, plcs, latestValues, plcVars, navigate }) {
  return (
    <Box sx={{ mb: 5 }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.75 }}>
        <Typography sx={{
          fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.08rem",
          letterSpacing: "-0.01em", color: "var(--text-main)",
        }}>
          {title}
        </Typography>
        <Box sx={{
          px: 1, py: 0.2, borderRadius: 999, fontSize: "0.68rem", fontWeight: 700,
          background: "var(--bg-soft)", border: "1px solid var(--border-color)",
          color: "var(--text-muted)",
        }}>
          {plcs.length} PLC{plcs.length !== 1 ? "s" : ""}
        </Box>
      </Stack>

      {plcs.length > 0 ? (
        <Grid container spacing={2}>
          {plcs.map((plc) => (
            <Grid item xs={12} sm={6} md={4} key={plc.id}>
              <PLCCard
                plc={plc}
                latestValues={latestValues[plc.id] || {}}
                importantVars={plcVars[plc.id] || []}
                onOpen={() => navigate(`/plc/${plc.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          variant="outlined"
          sx={{ p: 2.5, borderRadius: "var(--card-radius)", backgroundColor: "var(--bg-soft)", borderColor: "var(--border-color)" }}
        >
          <Typography variant="body2" sx={{ color: "var(--text-muted)" }}>
            No PLCs assigned here yet.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function PLC() {
  const [data, setData]               = useState(null);
  const [latestValues, setLatestValues] = useState({});
  const [plcVars, setPlcVars]         = useState({});
  const navigate                       = useNavigate();

  const fetchPLCs = async () => {
    try {
      const plcData = await api.get("/plc");
      // Guard against non-JSON responses (e.g. a proxy serving the SPA
      // fallback page while the backend is down) — keep showing the skeleton.
      if (!plcData || typeof plcData !== "object" || !("plcs" in plcData)) {
        throw new Error("Unexpected /plc response");
      }
      setData(plcData);

      const plcList =
        plcData.role === "admin"
          ? Object.values(plcData.plcs).flat()
          : plcData.plcs;

      const latestMap = {};
      const varsMap   = {};

      await Promise.all(
        plcList.map(async (plc) => {
          try {
            const [vals, vars] = await Promise.all([
              api.get(`/plc/${plc.id}/latest`).catch(() => ({})),
              api.get(`/plc/${plc.id}/important_vars`).catch(() => ({ important_vars: [] })),
            ]);
            latestMap[plc.id] = vals;
            varsMap[plc.id]   = vars?.important_vars || [];
          } catch {
            latestMap[plc.id] = {};
            varsMap[plc.id]   = [];
          }
        })
      );

      setLatestValues(latestMap);
      setPlcVars(varsMap);
    } catch (err) {
      console.error("fetchPLCs error", err);
    }
  };

  useEffect(() => {
    fetchPLCs();
    const id = setInterval(fetchPLCs, 60_000);
    return () => clearInterval(id);
  }, []);

  // Summary counts
  const { totalPLCs, onlineCount, offlineCount } = useMemo(() => {
    if (!data) return { totalPLCs: 0, onlineCount: 0, offlineCount: 0 };
    const plcList =
      data.role === "admin"
        ? Object.values(data.plcs || {}).flat()
        : (data.plcs || []);
    let online = 0;
    let offline = 0;
    for (const plc of plcList) {
      const s = getStatus(latestValues[plc.id] || {});
      if (s === "online")  online++;
      if (s === "offline") offline++;
    }
    return { totalPLCs: plcList.length, onlineCount: online, offlineCount: offline };
  }, [data, latestValues]);

  if (!data) {
    return (
      <div>
        <Navbar />
        <Box sx={{
          minHeight: "70vh", pt: "80px", px: { xs: 2, md: 3 }, pb: 6,
          background: "radial-gradient(circle at top left, var(--accent-glow), transparent 32%), var(--bg-main)",
        }}>
          <Box sx={{ maxWidth: 1400, mx: "auto" }}>
            <Skeleton
              variant="rounded"
              height={104}
              sx={{ borderRadius: "var(--card-radius)", mb: 4, bgcolor: "var(--bg-soft)" }}
            />
            <Skeleton
              variant="text"
              width={180}
              sx={{ mb: 1.5, bgcolor: "var(--bg-soft)" }}
            />
            <Grid container spacing={2}>
              {[0, 1, 2].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton
                    variant="rounded"
                    height={300}
                    sx={{ borderRadius: "var(--card-radius)", bgcolor: "var(--bg-soft)" }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <Box sx={{
        minHeight: "70vh", pt: "80px", px: { xs: 2, md: 3 }, pb: 6,
        background: "radial-gradient(circle at top left, var(--accent-glow), transparent 32%), var(--bg-main)",
      }}>
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>

          {/* ── Page header ── */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "flex-end" }}
            spacing={2.5}
            sx={{ mb: 4.5, mt: 1 }}
          >
            <Box>
              <Typography variant="overline" sx={{
                color: "var(--accent)", fontWeight: 700, letterSpacing: "0.14em", fontSize: "0.7rem",
              }}>
                PLC Dashboard
              </Typography>
              <Typography variant="h3" sx={{
                fontWeight: 700, color: "var(--text-main)", lineHeight: 1.1, mt: 0.5,
                fontSize: { xs: "1.9rem", md: "2.4rem" },
              }}>
                Welcome, {data.username}
              </Typography>
              <Typography variant="body2" sx={{ color: "var(--text-muted)", mt: 1 }}>
                {data.role === "admin"
                  ? "Overview of all client PLCs and their live readings."
                  : "Your PLCs and the latest important readings."}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.25} flexShrink={0}>
              <StatTile label="PLCs" value={totalPLCs} />
              <StatTile label="Online" value={onlineCount} tone="green" />
              <StatTile label="Offline" value={offlineCount} tone="red" />
            </Stack>
          </Stack>

          {/* ── PLC grid ── */}
          {data.role === "admin" ? (
            Object.entries(data.plcs)
              .filter(([client]) => client !== "admin")
              .map(([client, plcs]) => (
                <PLCSection
                  key={client}
                  title={client}
                  plcs={plcs}
                  latestValues={latestValues}
                  plcVars={plcVars}
                  navigate={navigate}
                />
              ))
          ) : (
            <PLCSection
              title="Your PLCs"
              plcs={data.plcs}
              latestValues={latestValues}
              plcVars={plcVars}
              navigate={navigate}
            />
          )}
        </Box>
      </Box>
      <Footer />
    </div>
  );
}

export default PLC;
